import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  useGetJobsLiveList,
  useGetPhasesLiveList,
} from '@/services/hooks/relay/live-lists'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { formatValue, getMaxDecimals } from './shared'
import { LineItem } from './useDocumentForPOMatchingData'

export type LineItemsTableRow = LineItem & {
  remainingQuantity?: string | null
  remainingAmount?: string | null
}

type NumberCellProps = {
  num: string | null | undefined
  displayName: string | undefined
  jobNumber?: string | null
}

function JobNumberCell({ num, displayName }: NumberCellProps) {
  const { document } = useDocumentContext()
  const { project } = useProjectContext()
  const orgId = document?.owner_org_id || ''
  const orgCredentialTypeId = project.org_credential_type_id || ''
  const { jobs } = useGetJobsLiveList({
    orgId: orgId || '',
    orgCredentialTypeId: orgCredentialTypeId || '',
    filters: num ? { num__eq: num } : undefined,
    enabled: !displayName && !!num,
  })
  const resolvedName = displayName || jobs[0]?.pd_display_name

  return (
    <Tooltip
      title={resolvedName ?? ''}
      disableHoverListener={!resolvedName}
      enterDelay={200}
      arrow
      placement="top"
    >
      <TableCell sx={{ py: 0.5 }}>{num || '\u2014'}</TableCell>
    </Tooltip>
  )
}

function PhaseNumberCell({ num, displayName, jobNumber }: NumberCellProps) {
  const { document } = useDocumentContext()
  const { project } = useProjectContext()
  const orgId = document?.owner_org_id || ''
  const orgCredentialTypeId = project.org_credential_type_id || ''

  const { phases } = useGetPhasesLiveList({
    orgId,
    orgCredentialTypeId,
    jobNum: jobNumber || '',
    filters: num ? { num__eq: num } : undefined,
    enabled: !displayName && !!num,
  })
  const resolvedName = displayName || phases[0]?.pd_display_name

  return (
    <Tooltip
      title={resolvedName ?? ''}
      disableHoverListener={!resolvedName}
      enterDelay={200}
      arrow
      placement="top"
    >
      <TableCell sx={{ py: 0.5 }}>{num || '\u2014'}</TableCell>
    </Tooltip>
  )
}

type LineItemsTableProps = {
  rows: LineItemsTableRow[]
  rowRefCallback?: (rowId: string) => (el: HTMLTableRowElement | null) => void
  highlightedRowIds?: Set<string>
  showJobColumn?: boolean
  showPhaseColumn?: boolean
}

export default function LineItemsTable({
  rows,
  rowRefCallback,
  highlightedRowIds,
  showJobColumn = true,
  showPhaseColumn = true,
}: LineItemsTableProps) {
  const hasRemainingQuantity = rows.some(
    (row) => row.remainingQuantity !== undefined,
  )
  const hasRemainingAmount = rows.some(
    (row) => row.remainingAmount !== undefined,
  )
  const hasGroupedHeader = hasRemainingQuantity || hasRemainingAmount

  const qtyDecimals = getMaxDecimals(rows.map((row) => row.quantity))
  const rateDecimals = Math.max(2, getMaxDecimals(rows.map((row) => row.rate)))
  const amountDecimals = Math.max(
    2,
    getMaxDecimals(rows.map((row) => row.amount)),
  )

  return (
    <>
      {rows.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 1, fontStyle: 'italic' }}
        >
          No line items
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ fontWeight: 600, pl: 0 }}
                  rowSpan={hasGroupedHeader ? 2 : 1}
                >
                  Description
                </TableCell>
                {showJobColumn && (
                  <TableCell
                    sx={{ fontWeight: 600 }}
                    rowSpan={hasGroupedHeader ? 2 : 1}
                  >
                    Job
                  </TableCell>
                )}
                {showPhaseColumn && (
                  <TableCell
                    sx={{ fontWeight: 600 }}
                    rowSpan={hasGroupedHeader ? 2 : 1}
                  >
                    Phase
                  </TableCell>
                )}
                {hasRemainingQuantity ? (
                  <TableCell
                    sx={{ fontWeight: 600 }}
                    align="center"
                    colSpan={2}
                  >
                    Quantity
                  </TableCell>
                ) : (
                  <TableCell
                    sx={{ fontWeight: 600 }}
                    align="right"
                    rowSpan={hasGroupedHeader ? 2 : 1}
                  >
                    Quantity
                  </TableCell>
                )}
                <TableCell
                  sx={{ fontWeight: 600 }}
                  align="right"
                  rowSpan={hasGroupedHeader ? 2 : 1}
                >
                  Rate
                </TableCell>
                {hasRemainingAmount ? (
                  <TableCell
                    sx={{ fontWeight: 600, pr: 0 }}
                    align="center"
                    colSpan={2}
                  >
                    Amount
                  </TableCell>
                ) : (
                  <TableCell
                    sx={{ fontWeight: 600, pr: 0 }}
                    align="right"
                    rowSpan={hasGroupedHeader ? 2 : 1}
                  >
                    Amount
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.rowId}
                  ref={rowRefCallback?.(row.rowId)}
                  sx={{
                    '& td': { borderBottom: 0, verticalAlign: 'middle' },
                    ...(highlightedRowIds?.has(row.rowId) && {
                      bgcolor: 'action.selected',
                    }),
                    transition: 'background-color 180ms ease',
                  }}
                >
                  <TableCell sx={{ py: 0.5, pl: 0, maxWidth: 200 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ lineHeight: 'inherit' }}
                      title={row.description ?? undefined}
                    >
                      {row.description ?? '—'}
                    </Typography>
                  </TableCell>
                  {showJobColumn && (
                    <JobNumberCell
                      num={row.jobNumber}
                      displayName={row.jobDisplayName}
                    />
                  )}
                  {showPhaseColumn && (
                    <PhaseNumberCell
                      num={row.phaseNumber}
                      displayName={row.phaseDisplayName}
                      jobNumber={row.jobNumber}
                    />
                  )}
                  {hasRemainingQuantity ? (
                    <>
                      <Tooltip
                        title="Remaining"
                        enterDelay={200}
                        arrow
                        placement="top"
                      >
                        <TableCell
                          align="right"
                          sx={{ py: 0.5, fontWeight: 600 }}
                        >
                          {formatValue(row.remainingQuantity ?? null, {
                            decimals: qtyDecimals,
                          })}
                        </TableCell>
                      </Tooltip>
                      <Tooltip
                        title="Ordered"
                        enterDelay={200}
                        arrow
                        placement="top"
                      >
                        <TableCell
                          align="right"
                          sx={{
                            py: 0.5,
                            borderLeft: '1px solid',
                            borderColor: 'divider',
                            color: 'text.secondary',
                          }}
                        >
                          {formatValue(row.quantity, { decimals: qtyDecimals })}
                        </TableCell>
                      </Tooltip>
                    </>
                  ) : (
                    <TableCell align="right" sx={{ py: 0.5 }}>
                      {formatValue(row.quantity, { decimals: qtyDecimals })}
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    {formatValue(row.rate, {
                      decimals: rateDecimals,
                      prefix: '$',
                    })}
                  </TableCell>
                  {hasRemainingAmount ? (
                    <>
                      <Tooltip
                        title="Remaining"
                        enterDelay={200}
                        arrow
                        placement="top"
                      >
                        <TableCell
                          align="right"
                          sx={{ py: 0.5, fontWeight: 600 }}
                        >
                          {formatValue(row.remainingAmount ?? null, {
                            decimals: amountDecimals,
                            prefix: '$',
                          })}
                        </TableCell>
                      </Tooltip>
                      <Tooltip
                        title="Amount"
                        enterDelay={200}
                        arrow
                        placement="top"
                      >
                        <TableCell
                          align="right"
                          sx={{
                            py: 0.5,
                            borderLeft: '1px solid',
                            borderColor: 'divider',
                            color: 'text.secondary',
                          }}
                        >
                          {formatValue(row.amount, {
                            decimals: amountDecimals,
                            prefix: '$',
                          })}
                        </TableCell>
                      </Tooltip>
                    </>
                  ) : (
                    <TableCell align="right" sx={{ py: 0.5, pr: 0 }}>
                      {formatValue(row.amount, {
                        decimals: amountDecimals,
                        prefix: '$',
                      })}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}
